import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Globe2,
  RefreshCw,
  Search,
  Sparkles,
  Download,
  Send,
  ShieldCheck,
  History,
} from "lucide-react";
import { toast } from "sonner";

type Status = "sent" | "verified" | "failed";
type GlobalRow = {
  id: string;
  email: string;
  full_name: string | null;
  country: string | null;
  status: Status;
  verified_at: string | null;
  last_error: string | null;
  attempts: number;
  sent_count: number;
  last_sent_at: string | null;
  created_at: string;
};
type ProRow = {
  id: string;
  email: string;
  full_name: string | null;
  interest: string | null;
  created_at: string;
};
type AttemptRow = {
  id: string;
  email: string;
  action: string;
  success: boolean;
  error: string | null;
  country: string | null;
  created_at: string;
};

const STATUS_LABELS: Record<Status, string> = {
  sent: "OTP sent",
  verified: "Verified",
  failed: "Failed",
};
const STATUS_VARIANT: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  sent: "secondary",
  verified: "default",
  failed: "destructive",
};

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

function toCsv(rows: Record<string, unknown>[], cols: string[]) {
  const esc = (v: unknown) => {
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
}

function download(name: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

type FreeQuizRow = {
  id: string;
  name: string;
  email: string;
  mobile: string | null;
  age_band: string | null;
  magnet: string;
  archetype_primary: string | null;
  archetype_secondary: string | null;
  created_at: string;
};

export default function AdminWaitlists() {
  const [loading, setLoading] = useState(true);
  const [global, setGlobal] = useState<GlobalRow[]>([]);
  const [pro, setPro] = useState<ProRow[]>([]);
  const [attempts, setAttempts] = useState<AttemptRow[]>([]);
  const [freeQuiz, setFreeQuiz] = useState<FreeQuizRow[]>([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | Status>("all");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [forceTarget, setForceTarget] = useState<GlobalRow | null>(null);

  const load = async () => {
    setLoading(true);
    const [g, p, a, f] = await Promise.all([
      supabase
        .from("global_waitlist")
        .select(
          "id,email,full_name,country,status,verified_at,last_error,attempts,sent_count,last_sent_at,created_at",
        )
        .order("created_at", { ascending: false })
        .limit(1000),
      supabase
        .from("pro_waitlist")
        .select("id,email,full_name,interest,created_at")
        .order("created_at", { ascending: false })
        .limit(1000),
      supabase
        .from("waitlist_attempts")
        .select("id,email,action,success,error,country,created_at")
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("free_quiz_leads")
        .select("id,name,email,mobile,age_band,magnet,archetype_primary,archetype_secondary,created_at")
        .order("created_at", { ascending: false })
        .limit(1000),
    ]);
    if (g.error) toast.error(`Global waitlist: ${g.error.message}`);
    if (p.error) toast.error(`Pro waitlist: ${p.error.message}`);
    if (a.error) toast.error(`Attempts: ${a.error.message}`);
    if (f.error) toast.error(`Free quiz leads: ${f.error.message}`);
    setGlobal((g.data ?? []) as GlobalRow[]);
    setPro((p.data ?? []) as ProRow[]);
    setAttempts((a.data ?? []) as AttemptRow[]);
    setFreeQuiz((f.data ?? []) as FreeQuizRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const q = query.trim().toLowerCase();
  const filteredGlobal = useMemo(() => {
    return global.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (countryFilter !== "all" && (r.country ?? "Unknown") !== countryFilter) return false;
      if (!q) return true;
      return (
        r.email.toLowerCase().includes(q) ||
        (r.full_name ?? "").toLowerCase().includes(q) ||
        (r.country ?? "").toLowerCase().includes(q)
      );
    });
  }, [global, q, statusFilter, countryFilter]);

  const filteredPro = useMemo(
    () =>
      !q
        ? pro
        : pro.filter(
            (r) =>
              r.email.toLowerCase().includes(q) ||
              (r.full_name ?? "").toLowerCase().includes(q) ||
              (r.interest ?? "").toLowerCase().includes(q),
          ),
    [pro, q],
  );

  const byCountry = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of global) {
      const k = r.country ?? "Unknown";
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
  }, [global]);

  const countryBreakdown = useMemo(() => {
    const m = new Map<string, { sent: number; verified: number; failed: number }>();
    for (const r of global) {
      const k = r.country ?? "Unknown";
      const row = m.get(k) ?? { sent: 0, verified: 0, failed: 0 };
      row[r.status] += 1;
      m.set(k, row);
    }
    return Array.from(m.entries())
      .map(([country, c]) => ({ country, ...c, total: c.sent + c.verified + c.failed }))
      .sort((a, b) => b.total - a.total);
  }, [global]);

  const statusCounts = useMemo(() => {
    const c = { sent: 0, verified: 0, failed: 0 };
    for (const r of global) c[r.status] += 1;
    return c;
  }, [global]);

  const resend = async (row: GlobalRow) => {
    setBusyId(row.id);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-otp", {
        body: {
          action: "admin_resend",
          email: row.email,
          name: row.full_name,
          country: row.country,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(`OTP resent to ${row.email}`);
      await load();
    } catch (e: any) {
      const raw = e?.message ?? e?.context?.error ?? "";
      const msg = e?.context?.message as string | undefined;
      toast.error(
        msg ??
          (raw.includes("too_many_requests")
            ? "Rate-limited (3 codes / 10 min per email)."
            : raw.includes("forbidden")
              ? "You're not authorized."
              : "Could not resend code."),
      );
    } finally {
      setBusyId(null);
    }
  };

  const forceVerify = async (row: GlobalRow) => {
    setBusyId(row.id);
    try {
      const { data, error } = await supabase.functions.invoke("waitlist-otp", {
        body: {
          action: "admin_force_verify",
          email: row.email,
          name: row.full_name,
          country: row.country,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      toast.success(
        (data as any)?.alreadyVerified
          ? `${row.email} was already verified`
          : `${row.email} force-verified & welcome email sent`,
      );
      setForceTarget(null);
      await load();
    } catch (e: any) {
      const raw = e?.message ?? e?.context?.error ?? "";
      toast.error(raw.includes("forbidden") ? "Not authorized" : "Could not force-verify");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHead
        title="Waitlists | Admin | WeKIT™"
        description="Global launch and Pro waitlist signups."
        path="/admin/waitlists"
        noIndex
      />

      <header className="border-b">
        <div className="container py-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link
              to="/admin"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
            >
              <ArrowLeft className="h-3 w-3" /> Admin Hub
            </Link>
            <h1 className="text-3xl font-bold tracking-tight mt-1">Waitlists</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Global launch (country-by-country) and WeKIT™ Pro waitlist signups.
            </p>
          </div>
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="container py-8 space-y-6">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Card className="rounded-3xl">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between text-muted-foreground text-sm">
                <span>Total signups</span>
                <Globe2 className="h-5 w-5 text-primary" />
              </div>
              <div className="mt-2 text-3xl font-bold">{global.length.toLocaleString()}</div>
              <div className="mt-1 text-xs text-muted-foreground">{byCountry.length} countries</div>
            </CardContent>
          </Card>
          <Card className="rounded-3xl">
            <CardContent className="pt-6">
              <div className="text-muted-foreground text-sm">Verified</div>
              <div className="mt-2 text-3xl font-bold text-primary">
                {statusCounts.verified.toLocaleString()}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-3xl">
            <CardContent className="pt-6">
              <div className="text-muted-foreground text-sm">Pending (OTP sent)</div>
              <div className="mt-2 text-3xl font-bold">{statusCounts.sent.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card className="rounded-3xl">
            <CardContent className="pt-6">
              <div className="text-muted-foreground text-sm">Failed</div>
              <div className="mt-2 text-3xl font-bold text-destructive">
                {statusCounts.failed.toLocaleString()}
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-3xl">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between text-muted-foreground text-sm">
                <span>Pro waitlist</span>
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div className="mt-2 text-3xl font-bold">{pro.length.toLocaleString()}</div>
            </CardContent>
          </Card>
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by email, name, country…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 h-11"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
            <SelectTrigger className="w-[170px] h-11">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">OTP sent</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-[200px] h-11">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">All countries</SelectItem>
              {byCountry.map(([c, n]) => (
                <SelectItem key={c} value={c}>
                  {c} ({n})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="global" className="space-y-4">
          <TabsList>
            <TabsTrigger value="global">Global launch ({filteredGlobal.length})</TabsTrigger>
            <TabsTrigger value="pro">Pro waitlist ({pro.length})</TabsTrigger>
            <TabsTrigger value="countries">By country</TabsTrigger>
            <TabsTrigger value="attempts">Attempts log</TabsTrigger>
            <TabsTrigger value="freequiz">Free quiz ({freeQuiz.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="freequiz">
            <Card>
              <CardHeader>
                <CardTitle>Free quiz leads</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {freeQuiz.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No one has taken the free quiz yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-2 pr-4">Name</th>
                        <th className="py-2 pr-4">Email</th>
                        <th className="py-2 pr-4">Mobile</th>
                        <th className="py-2 pr-4">Band</th>
                        <th className="py-2 pr-4">Leaning</th>
                        <th className="py-2 pr-4">Taken</th>
                      </tr>
                    </thead>
                    <tbody>
                      {freeQuiz.map((r) => (
                        <tr key={r.id} className="border-t border-border/50">
                          <td className="py-2 pr-4">{r.name}</td>
                          <td className="py-2 pr-4">{r.email}</td>
                          <td className="py-2 pr-4">{r.mobile ?? "—"}</td>
                          <td className="py-2 pr-4">{r.age_band ?? "—"}</td>
                          <td className="py-2 pr-4">
                            {r.archetype_primary
                              ? `${r.archetype_primary}${r.archetype_secondary ? ` · ${r.archetype_secondary}` : ""}`
                              : "Not finished"}
                          </td>
                          <td className="py-2 pr-4">{new Date(r.created_at).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="global">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Globe2 className="h-4 w-4" /> Global launch signups
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    download(
                      "global-waitlist.csv",
                      toCsv(filteredGlobal, [
                        "created_at",
                        "email",
                        "full_name",
                        "country",
                        "status",
                        "verified_at",
                        "attempts",
                        "sent_count",
                        "last_error",
                      ]),
                    )
                  }
                >
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : filteredGlobal.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No signups match your filters.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Attempts</TableHead>
                        <TableHead>Last sent</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredGlobal.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">
                            {r.email}
                            {r.last_error && (
                              <div className="text-[10px] text-destructive mt-0.5">
                                {r.last_error}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{r.full_name ?? "—"}</TableCell>
                          <TableCell>
                            {r.country ? <Badge variant="secondary">{r.country}</Badge> : "—"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_VARIANT[r.status]}>
                              {STATUS_LABELS[r.status]}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {r.attempts}/{r.sent_count}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {fmtDate(r.last_sent_at ?? r.created_at)}
                          </TableCell>
                          <TableCell className="text-right space-x-2 whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busyId === r.id || r.status === "verified"}
                              onClick={() => resend(r)}
                            >
                              <Send className="h-3 w-3 mr-1" /> Resend
                            </Button>
                            <Button
                              size="sm"
                              disabled={busyId === r.id || r.status === "verified"}
                              onClick={() => setForceTarget(r)}
                            >
                              <ShieldCheck className="h-3 w-3 mr-1" /> Force verify
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="pro">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" /> Pro waitlist signups
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    download(
                      "pro-waitlist.csv",
                      toCsv(filteredPro, ["created_at", "email", "full_name", "interest"]),
                    )
                  }
                >
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                ) : filteredPro.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No signups yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Most-wanted feature</TableHead>
                        <TableHead>Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPro.map((r) => (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">{r.email}</TableCell>
                          <TableCell>{r.full_name ?? "—"}</TableCell>
                          <TableCell>{r.interest ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {fmtDate(r.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="countries">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe2 className="h-4 w-4" /> Onboarding progress by country
                </CardTitle>
              </CardHeader>
              <CardContent>
                {countryBreakdown.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No signups yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Country</TableHead>
                        <TableHead className="text-right">Verified</TableHead>
                        <TableHead className="text-right">Pending</TableHead>
                        <TableHead className="text-right">Failed</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {countryBreakdown.map((c) => (
                        <TableRow key={c.country}>
                          <TableCell>{c.country}</TableCell>
                          <TableCell className="text-right font-mono text-primary">
                            {c.verified}
                          </TableCell>
                          <TableCell className="text-right font-mono">{c.sent}</TableCell>
                          <TableCell className="text-right font-mono text-destructive">
                            {c.failed}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            {c.total}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="attempts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-4 w-4" /> Recent OTP attempts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {attempts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No attempts logged yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>When</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Result</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {attempts.slice(0, 200).map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="text-xs text-muted-foreground">
                            {fmtDate(a.created_at)}
                          </TableCell>
                          <TableCell className="font-medium">{a.email}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{a.action}</Badge>
                          </TableCell>
                          <TableCell>{a.country ?? "—"}</TableCell>
                          <TableCell>
                            {a.success ? (
                              <Badge>ok</Badge>
                            ) : (
                              <Badge variant="destructive">{a.error ?? "failed"}</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      <AlertDialog open={!!forceTarget} onOpenChange={(o) => !o && setForceTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Force-verify this signup?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks <span className="font-medium">{forceTarget?.email}</span> as verified
              without an OTP check and sends them a welcome email. Use only when you've
              confirmed the signup another way.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => forceTarget && forceVerify(forceTarget)}>
              Force verify
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
